import { DestroyRef, Injectable, computed, effect, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationCancel, NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';

import { AuthService } from '../auth/auth';
import { StateStoreService } from '../state-store/state-store';
import { ProfileCompletionFlowService } from '../profile-completion-flow/profile-completion-flow.service';

/**
 * "Boot is taking a while" hint shown under the spinner. 12 s clears
 * the noisy 5–10 s legitimate cold-start window but fires well before
 * the user starts hunting for the reload button on their own.
 */
const SLOW_DEADLINE_MS = 12_000;

/**
 * "Boot is wedged — show the error UI" deadline. 25 s is long enough
 * to outlast a slow-3G cold start (typically 8–14 s on the slowest
 * legitimate paths we've measured) and the 10 s
 * `AMPLIFY_CALL_TIMEOUT_MS` ceiling on each Amplify call — so by the
 * time `phase` flips to `stalled`, every fast-failure path has already
 * had two chances to recover. What's left at 25 s is genuinely stuck.
 */
const STALLED_DEADLINE_MS = 25_000;

/** The phase of the boot the user is currently looking at. */
export type BootPhase = 'booting' | 'slow' | 'stalled';

/**
 * Boot-readiness + escape hatch for the app shell.
 *
 * Replaces the previous arrangement where `showBootLoader()` in
 * `app.ts` watched four signals (`authService.isLoading`,
 * `stateStore.isStateReady`, an in-component `initialNavDone`,
 * `profileFlow.initializing`) and had no notion of "loader has been
 * spinning forever — show the user a way out." That arrangement
 * stranded users when any one of those signals never flipped
 * (Amplify hang, hung `/whoami`, guard-throw navigation error).
 *
 * Responsibilities now live here:
 *   - `isBooting` — the single boot-readiness predicate. Composed
 *     from the same four signals; `app.ts` reads it instead of
 *     composing them again.
 *   - `navDone` — set on the first `NavigationEnd` AND on non-chunk
 *     `NavigationError` (via `markInitialNavSettled` called from
 *     `handleRouterNavigationError`). Without the error branch,
 *     a guard-throw on the first route would leave `navDone` false
 *     forever and the loader would spin.
 *   - `phase` — `'booting' | 'slow' | 'stalled'`. Two-stage deadline.
 *     `<app-loader>` reads `'slow'` to render a "Still loading..."
 *     hint; `<app-boot-error>` mounts on `'stalled'`.
 *   - `stuckSignals` — diagnostic readout (`['auth:fetchSession']`,
 *     `['state', 'nav']`) surfaced to `<app-boot-error>` and to
 *     `console.error` when the stalled-boot deadline fires. Saves a
 *     remote-debugging round-trip when the next user reports.
 *
 * Architecture note: this service OWNS its readiness signals rather
 * than observing component-level state, so it can be unit-tested in
 * isolation and so there's a single source of truth. `app.ts` ends
 * up with no boot-state of its own — only layout concerns.
 */
@Injectable({ providedIn: 'root' })
export class BootHealthService {
  private readonly auth = inject(AuthService);
  private readonly stateStore = inject(StateStoreService);
  private readonly profile = inject(ProfileCompletionFlowService);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);

  private readonly _navDone = signal(false);
  private readonly _phase = signal<BootPhase>('booting');
  private readonly _dismissed = signal(false);

  /**
   * Wall-clock timestamp of when `isBooting()` most recently became
   * true (or 0 while not booting). Powers the visibility-API
   * backstop — Chrome aggressively throttles `setTimeout` in
   * backgrounded tabs (intensive throttling kicks in after 5 min of
   * being hidden, but even less-throttled background tabs see
   * timers fire late). A user who opens the app, switches away,
   * and returns 30 s later should immediately see the stalled UI
   * if 25 s has actually elapsed — even if the `setTimeout`
   * scheduled by the deadline effect hasn't fired yet because of
   * throttling.
   *
   * Plain field rather than signal because it's only read by
   * `checkElapsedAndUpdatePhase`, never as a reactive dependency.
   */
  private _bootStartedAt = 0;

  /**
   * True while any boot-readiness signal hasn't settled.
   *
   * State-readiness (`/whoami`) and profile-completion are gated on
   * `auth.isAuthenticated()` — unauthenticated users belong on the
   * public routes (e.g. `/login`) and aren't waiting on those. This
   * mirrors the original `app.ts:showBootLoader` shape that conditioned
   * the state/profile checks on `showAppContainer` (which expanded to
   * `isAuthenticated && !isLoading`).
   *
   * Without this gate, users who boot unauthenticated — brand-new
   * visitors, session-expired returners, Amplify-timed-out users —
   * would wait forever on `isStateReady`, which is only flipped by
   * `loadWhoAmI`'s success path. `loadWhoAmI` doesn't fire for
   * unauthenticated users (see `StateStoreService.setupAuthSubscription`),
   * so the flag never goes true on its own. The state-store has its
   * own defense (sets the flag on auth-transition-to-false), but
   * this gate is the load-bearing fix that doesn't depend on
   * cross-service ordering.
   */
  readonly isBooting = computed(() => {
    if (this.auth.isLoading()) return true;
    if (this.profile.initializing()) return true;
    if (!this.auth.isAuthenticated()) {
      // Unauth path: only auth-settling and the first nav gate the
      // loader. The user lands on /login or another public route.
      return !this._navDone();
    }
    // Auth path: also wait on /whoami before unveiling the shell.
    return !this.stateStore.isStateReady() || !this._navDone();
  });

  /** Two-stage boot status for the shell to render against. */
  readonly phase = this._phase.asReadonly();

  /**
   * Names of the source signals currently blocking boot. Used by the
   * stalled-boot diagnostic in `<app-boot-error>` and by the
   * structured `console.error` emitted when `phase` flips to
   * `stalled`. The `auth` entry includes the in-flight Amplify call
   * sub-state (`auth:fetchSession`, `auth:getCurrentUser`, ...) so a
   * single screenshot from the affected user pins down the specific
   * call hanging without a remote debugging session.
   *
   * `state` and `profile` are only reported as stuck when the user
   * IS authenticated — those signals are irrelevant on the
   * unauthenticated path and would be false-positives otherwise.
   */
  readonly stuckSignals = computed<readonly string[]>(() => {
    const out: string[] = [];
    if (this.auth.isLoading()) {
      const call = this.auth.currentAuthCall();
      out.push(call === 'idle' ? 'auth' : `auth:${call}`);
    }
    if (this.auth.isAuthenticated()) {
      if (!this.stateStore.isStateReady()) out.push('state');
      if (this.profile.initializing()) out.push('profile');
    }
    if (!this._navDone()) out.push('nav');
    return out;
  });

  /** True from boot until the first NavigationEnd or non-chunk NavigationError. */
  readonly navDone = this._navDone.asReadonly();

  /**
   * Deadline effect — watches `isBooting()`. On true (the boot window
   * opens), schedules both timers. On false (boot completed), cancels
   * them. `onCleanup` runs both when the effect re-runs (next
   * `isBooting` flip) and when the service is destroyed; that's
   * exactly the lifecycle we want.
   *
   * Field initializer (rather than inside `start()`) because Angular
   * 20's `effect()` requires an injection context, which is available
   * during class construction but NOT inside arbitrary instance
   * methods. The effect immediately reads `isBooting()` which is
   * false-then-true at boot (signals haven't loaded yet → loading
   * → settled), so the deadline starts as soon as anyone injects
   * this service — i.e. when AppComponent mounts. Subscribers can
   * still call `start()` to wire up router events; the deadline
   * timer is always live.
   */
  private readonly _deadlineEffect = effect(onCleanup => {
    if (!this.isBooting()) {
      this._phase.set('booting');
      this._dismissed.set(false);
      this._bootStartedAt = 0;
      return;
    }

    // Stamp the boot start once per boot window. The visibility
    // backstop reads this to compute elapsed time independently of
    // the setTimeout clock (which Chrome can throttle).
    if (this._bootStartedAt === 0) {
      this._bootStartedAt = Date.now();
    }

    const slowTimer = setTimeout(() => {
      if (this._dismissed()) return;
      this._phase.set('slow');
    }, SLOW_DEADLINE_MS);

    const stalledTimer = setTimeout(() => {
      if (this._dismissed()) return;
      this._phase.set('stalled');
      // Structured log so a screenshot from the affected user names
      // the exact stuck source. Shape designed to be picked up by
      // any future logging service unchanged.
      console.error('[boot-health] stalled', {
        stuckSignals: this.stuckSignals(),
        deadlineMs: STALLED_DEADLINE_MS,
      });
    }, STALLED_DEADLINE_MS);

    onCleanup(() => {
      clearTimeout(slowTimer);
      clearTimeout(stalledTimer);
    });
  });

  /**
   * Wall-clock backstop for the `setTimeout`-based deadlines.
   *
   * Chrome throttles `setTimeout` in backgrounded tabs (heavily
   * after 5 min hidden, lightly even sooner). A user who opens
   * the app, switches to another tab, and comes back 30 s later
   * could find the spinner still spinning because the 25 s
   * `stalled` timer was throttled and hasn't fired yet — even
   * though the wall clock has clearly passed the deadline.
   *
   * On every `visibilitychange → visible` transition, compute the
   * real elapsed time from `_bootStartedAt` and force the phase
   * forward if the wall clock has overshot the timer. This makes
   * the deadline robust against any timer throttling Chrome
   * decides to apply.
   *
   * Also handles bfcache restoration: when Chrome restores a page
   * from in-memory bfcache, `visibilitychange` fires as the page
   * becomes visible again, so we re-evaluate without needing a
   * separate `pageshow` listener.
   */
  private setupVisibilityBackstop(doc: Document): void {
    const handler = () => {
      if (doc.visibilityState !== 'visible') return;
      this.checkElapsedAndUpdatePhase();
    };
    doc.addEventListener('visibilitychange', handler);
    // Tear down on service destruction so test envs don't leak
    // listeners between specs.
    this.destroyRef.onDestroy(() => doc.removeEventListener('visibilitychange', handler));
  }

  private checkElapsedAndUpdatePhase(): void {
    if (!this.isBooting() || this._dismissed() || this._bootStartedAt === 0) return;
    const elapsed = Date.now() - this._bootStartedAt;
    if (elapsed >= STALLED_DEADLINE_MS && this._phase() !== 'stalled') {
      this._phase.set('stalled');
      console.error('[boot-health] stalled (wall-clock backstop)', {
        stuckSignals: this.stuckSignals(),
        elapsedMs: elapsed,
      });
    } else if (elapsed >= SLOW_DEADLINE_MS && this._phase() === 'booting') {
      this._phase.set('slow');
    }
  }

  /**
   * Wire router events to `navDone`. Called once from `app.ts`
   * `ngOnInit`. Idempotent. Separate from the constructor so the
   * service can be unit-tested without a real Router instance.
   */
  private started = false;
  start(): void {
    if (this.started) return;
    this.started = true;

    if (typeof document !== 'undefined') {
      this.setupVisibilityBackstop(document);
    }

    // Subscribe to BOTH NavigationEnd (success) and NavigationCancel
    // (a guard returned `false` or a `UrlTree` redirect). Without the
    // cancel branch, a guard that rejects without throwing would
    // leave `_navDone` false forever — `NavigationEnd` doesn't fire
    // for cancelled navigations. For UrlTree redirects, the
    // subsequent navigation produces its own NavigationEnd, but the
    // CANCEL of the original still flips us — the redirect is just
    // additional confirmation. NavigationError is intentionally NOT
    // here: it's handled by `handleRouterNavigationError` in
    // `chunk-reload.utils.ts`, which decides whether to mark settled
    // (non-chunk errors) or to reload (chunk errors, which expect
    // the loader to keep showing during the recovery navigation).
    this.router.events
      .pipe(
        filter(
          (event): event is NavigationEnd | NavigationCancel =>
            event instanceof NavigationEnd || event instanceof NavigationCancel
        ),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => this._navDone.set(true));

    // Synchronous-early case: NavigationEnd may have fired before
    // `start()` was called (Angular's initial navigation resolves
    // inside the bootstrap path on fast localhost).
    if (this.router.navigated) {
      this._navDone.set(true);
    }
  }

  /**
   * Called by `handleRouterNavigationError` when a first-navigation
   * error is NOT a chunk-load error (guard threw, resolver threw,
   * unauthorized redirect that itself failed, etc.). Without this,
   * `navDone` would stay false forever — `NavigationEnd` doesn't fire
   * on `NavigationError` — and `isBooting` would never settle.
   *
   * Chunk errors are deliberately NOT routed here: they trigger a
   * fresh-shell reload via `__reloadWithFreshShell`, and we want the
   * loader to keep showing during that reload, not the boot-error
   * UI.
   */
  markInitialNavSettled(): void {
    this._navDone.set(true);
  }

  /**
   * "Keep waiting" — user-driven dismissal of the stalled panel.
   * Sets a one-shot flag so the deadline effect won't re-fire
   * `slow`/`stalled` for the rest of this boot window. Resets when
   * `isBooting` flips back to false (next boot cycle gets a fresh
   * deadline) or when the user explicitly retries.
   */
  dismissStalled(): void {
    this._dismissed.set(true);
    this._phase.set('slow');
  }
}
